import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CountdownTimer } from "@/components/CountdownTimer";
import { Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface Promotion {
  id: string;
  name: string;
  discount_percentage: number;
  end_date: string;
  is_active: boolean;
}

export function SiteWidePromotionBanner() {
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSiteWidePromotion();

    // Subscribe to realtime promotion changes
    const channel = supabase
      .channel('site-wide-promotions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'promotions'
        },
        () => {
          loadSiteWidePromotion();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadSiteWidePromotion = async () => {
    // Get active promotion with no linked courses (site-wide)
    const { data: promotions } = await supabase
      .from('promotions')
      .select('id, name, discount_percentage, end_date, is_active')
      .eq('is_active', true)
      .gte('end_date', new Date().toISOString())
      .lte('start_date', new Date().toISOString());

    if (!promotions || promotions.length === 0) {
      setPromotion(null);
      return;
    }

    // Check if any promotion has no linked courses (site-wide)
    for (const promo of promotions) {
      const { data: linkedCourses } = await supabase
        .from('promotion_courses')
        .select('id')
        .eq('promotion_id', promo.id)
        .limit(1);

      if (!linkedCourses || linkedCourses.length === 0) {
        setPromotion(promo);
        return;
      }
    }

    setPromotion(null);
  };

  if (!promotion) return null;

  return (
    <div 
      className="fixed top-[60px] left-0 right-0 z-40 h-[60px] bg-gradient-to-r from-primary/20 via-primary/15 to-primary/10 border-b border-primary/30 backdrop-blur-md animate-fade-in cursor-pointer hover:from-primary/25 hover:via-primary/20 hover:to-primary/15 transition-all"
      onClick={() => navigate('/courses')}
    >
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 animate-pulse" />
      
      <div className="relative h-full container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full gap-6">
          {/* Left - Promotion info */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Tag className="w-5 h-5 text-primary flex-shrink-0" />
            <Badge variant="default" className="text-xs font-bold px-3 py-1 h-5 shadow-lg">
              {promotion.discount_percentage}% OFF
            </Badge>
            <span className="text-sm font-semibold text-foreground truncate">
              🎉 {promotion.name} - {promotion.discount_percentage}% OFF All Courses
            </span>
            <span className="text-sm text-primary font-medium whitespace-nowrap hidden md:inline">
              Limited Time Only!
            </span>
          </div>
          
          {/* Right - Countdown */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap hidden sm:inline">
              Offer ends in:
            </span>
            <div className="scale-75 origin-right">
              <CountdownTimer endDate={promotion.end_date} countdownType="hours" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
